import React, { useState, useEffect } from 'react'
import { gql, useMutation, useLazyQuery } from '@apollo/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Edit, X, ArrowUp, ArrowDown, Save } from 'lucide-react'
import { SET_LINKED_PRODUCTS } from '../productMutations'
import { GET_PRODUCT_DETAILS } from '../productQueries'

// --- Types ---

interface LinkedProductsCardProps {
  productId: number
  linkedProducts: Array<{
    id: number
    link_type: string
    display_order: number
    target_product: {
      id: number
      advanced_product?: {
        advanced_product_profiles: Array<{ name: string; language: string }>
        images: Array<{ src_thumbnail: string }>
      } | null
      single_product?: {
        product_profiles: Array<{ name: string; language: string }>
        images: Array<{ src_thumbnail: string }>
      } | null
    }
  }>
  language: string
}

type LinkType = 'ACCESSORY' | 'GOES_WELL_WITH' | 'ALTERNATIVE'

interface LinkedProductLocal {
  targetProductId: number
  targetProductName: string
  targetProductImage?: string
  link_type: LinkType
  display_order: number
}

// --- Search Query ---

const SEARCH_PRODUCTS = gql`
  query SearchProducts($where: ProductContainerWhereInput, $take: Int) {
    findManyProductContainer(where: $where, take: $take) {
      id
      advanced_product {
        advanced_product_profiles {
          name
          language
        }
        images(take: 1, orderBy: { display_order: asc }) {
          src_thumbnail
        }
      }
      single_product {
        product_profiles {
          name
          language
        }
        images(take: 1) {
          src_thumbnail
        }
      }
    }
  }
`

// --- Helpers ---

const LINK_TYPE_LABELS: Record<LinkType, string> = {
  ACCESSORY: 'Accessories',
  GOES_WELL_WITH: 'Goes Well With',
  ALTERNATIVE: 'Alternatives',
}

const LINK_TYPE_OPTIONS: LinkType[] = [
  'ACCESSORY',
  'GOES_WELL_WITH',
  'ALTERNATIVE',
]

function getProductName(targetProduct: any, language: string): string {
  const profiles =
    targetProduct.advanced_product?.advanced_product_profiles ||
    targetProduct.single_product?.product_profiles ||
    []
  const profile =
    profiles.find(
      (p: any) => p.language.toLowerCase() === language.toLowerCase()
    ) || profiles[0]
  return profile?.name || 'Unknown Product'
}

function getProductImage(targetProduct: any): string | undefined {
  return (
    targetProduct.advanced_product?.images?.[0]?.src_thumbnail ||
    targetProduct.single_product?.images?.[0]?.src_thumbnail
  )
}

function buildLocalProducts(
  linkedProducts: LinkedProductsCardProps['linkedProducts'],
  language: string
): LinkedProductLocal[] {
  return linkedProducts.map(lp => ({
    targetProductId: lp.target_product.id,
    targetProductName: getProductName(lp.target_product, language),
    targetProductImage: getProductImage(lp.target_product),
    link_type: lp.link_type as LinkType,
    display_order: lp.display_order,
  }))
}

// --- Component ---

export function LinkedProductsCard({
  productId,
  linkedProducts,
  language,
}: LinkedProductsCardProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [localProducts, setLocalProducts] = useState<LinkedProductLocal[]>(() =>
    buildLocalProducts(linkedProducts, language)
  )
  const [originalProducts, setOriginalProducts] = useState<
    LinkedProductLocal[]
  >(() => buildLocalProducts(linkedProducts, language))

  // Search state
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedLinkType, setSelectedLinkType] = useState<LinkType>('ACCESSORY')
  const [isSearchFocused, setIsSearchFocused] = useState(false)

  // Keep in sync with prop changes when not editing
  useEffect(() => {
    if (!isEditing) {
      const built = buildLocalProducts(linkedProducts, language)
      setLocalProducts(built)
      setOriginalProducts(built)
    }
  }, [linkedProducts, language, isEditing])

  // Apollo
  const [searchProducts, { data: searchData, loading: searchLoading }] =
    useLazyQuery(SEARCH_PRODUCTS, {
      fetchPolicy: 'cache-and-network',
    })

  const [setLinkedProducts, { loading: saving }] = useMutation(
    SET_LINKED_PRODUCTS,
    {
      refetchQueries: ['GET_PRODUCT_DETAILS'],
      onCompleted: () => {
        setIsEditing(false)
      },
    }
  )

  // Search results (excluding current product and already-linked products)
  const existingKeys = new Set(
    localProducts.map(p => `${p.targetProductId}-${p.link_type}`)
  )
  const searchResults: Array<{
    id: number
    name: string
    image?: string
  }> = (searchData?.findManyProductContainer || [])
    .filter(
      (p: any) => p.id !== productId && !existingKeys.has(`${p.id}-${selectedLinkType}`)
    )
    .map((p: any) => ({
      id: p.id,
      name: getProductName(p, language),
      image: getProductImage(p),
    }))

  // --- Grouped products ---

  const groupedProducts = LINK_TYPE_OPTIONS.reduce(
    (acc, type) => {
      acc[type] = localProducts
        .filter(p => p.link_type === type)
        .sort((a, b) => a.display_order - b.display_order)
      return acc
    },
    {} as Record<LinkType, LinkedProductLocal[]>
  )

  // --- hasChanges ---

  const hasChanges =
    JSON.stringify(
      localProducts.map(p => ({
        targetProductId: p.targetProductId,
        link_type: p.link_type,
        display_order: p.display_order,
      }))
    ) !==
    JSON.stringify(
      originalProducts.map(p => ({
        targetProductId: p.targetProductId,
        link_type: p.link_type,
        display_order: p.display_order,
      }))
    )

  // --- Handlers ---

  const handleEdit = () => {
    setIsEditing(true)
  }

  const handleCancel = () => {
    setLocalProducts(originalProducts)
    setIsEditing(false)
    setSearchTerm('')
  }

  const handleSave = async () => {
    try {
      await setLinkedProducts({
        variables: {
          productId,
          linked_products: localProducts.map(p => ({
            targetProductId: p.targetProductId,
            link_type: p.link_type,
            display_order: p.display_order,
          })),
        },
      })
      setOriginalProducts(localProducts)
    } catch (error) {
      console.error('Error saving linked products:', error)
    }
  }

  const handleRemove = (targetProductId: number, linkType: LinkType) => {
    const updated = localProducts.filter(
      p =>
        !(p.targetProductId === targetProductId && p.link_type === linkType)
    )
    // Recalculate display_order within each group
    const recalculated = recalculateDisplayOrders(updated)
    setLocalProducts(recalculated)
  }

  const handleReorder = (
    linkType: LinkType,
    index: number,
    direction: 'up' | 'down'
  ) => {
    const group = groupedProducts[linkType]
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === group.length - 1)
    ) {
      return
    }

    const targetIndex = direction === 'up' ? index - 1 : index + 1

    // Swap within the group
    const newGroup = group.map(item => ({ ...item }))
    const temp = newGroup[index]
    newGroup[index] = newGroup[targetIndex]
    newGroup[targetIndex] = temp

    // Recalculate display_order for this group
    newGroup.forEach((item, i) => {
      item.display_order = i + 1
    })

    // Rebuild full list: replace this group's items
    const otherProducts = localProducts.filter(p => p.link_type !== linkType)
    setLocalProducts([...otherProducts, ...newGroup])
  }

  const handleAdd = (productToAdd: {
    id: number
    name: string
    image?: string
  }) => {
    const group = groupedProducts[selectedLinkType]
    const newProduct: LinkedProductLocal = {
      targetProductId: productToAdd.id,
      targetProductName: productToAdd.name,
      targetProductImage: productToAdd.image,
      link_type: selectedLinkType,
      display_order: group.length + 1,
    }
    setLocalProducts([...localProducts, newProduct])
    setSearchTerm('')
  }

  const handleSearch = (value: string) => {
    setSearchTerm(value)
    if (value.trim().length >= 2) {
      searchProducts({
        variables: {
          where: {
            OR: [
              {
                single_product: {
                  is: {
                    product_profiles: {
                      some: {
                        name: { contains: value.trim(), mode: 'insensitive' },
                      },
                    },
                  },
                },
              },
              {
                advanced_product: {
                  is: {
                    advanced_product_profiles: {
                      some: {
                        name: { contains: value.trim(), mode: 'insensitive' },
                      },
                    },
                  },
                },
              },
            ],
          },
          take: 10,
        },
      })
    }
  }

  // --- Helpers ---

  function recalculateDisplayOrders(
    products: LinkedProductLocal[]
  ): LinkedProductLocal[] {
    const byType: Record<string, LinkedProductLocal[]> = {}
    products.forEach(p => {
      if (!byType[p.link_type]) byType[p.link_type] = []
      byType[p.link_type].push(p)
    })
    const result: LinkedProductLocal[] = []
    Object.values(byType).forEach(group => {
      group
        .sort((a, b) => a.display_order - b.display_order)
        .forEach((item, i) => {
          result.push({ ...item, display_order: i + 1 })
        })
    })
    return result
  }

  // --- Render ---

  const hasAnyProducts = localProducts.length > 0
  const nonEmptyGroups = LINK_TYPE_OPTIONS.filter(
    type => groupedProducts[type].length > 0
  )

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle>Linked Products</CardTitle>
        <div className="flex space-x-2">
          {!isEditing && (
            <Button onClick={handleEdit} size="sm" variant="ghost">
              <Edit className="h-4 w-4 mr-2" />
            </Button>
          )}
          {isEditing && (
            <>
              <Button
                onClick={handleCancel}
                size="sm"
                variant="ghost"
                disabled={saving}
              >
                <X className="h-4 w-4 text-red-500" />
                Cancel
              </Button>
              {hasChanges && (
                <Button
                  onClick={handleSave}
                  size="sm"
                  variant="default"
                  disabled={saving}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </Button>
              )}
            </>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Read & Edit mode: grouped product list */}
          {hasAnyProducts ? (
            nonEmptyGroups.map(type => (
              <div key={type}>
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  {LINK_TYPE_LABELS[type]}
                </div>
                <div className="space-y-1">
                  {groupedProducts[type].map((product, index) => (
                    <div
                      key={`${product.link_type}-${product.targetProductId}`}
                      className="flex items-center justify-between p-2"
                    >
                      <div className="flex items-center space-x-3">
                        {product.targetProductImage ? (
                          <img
                            src={product.targetProductImage}
                            alt={product.targetProductName}
                            className="w-10 h-10 object-cover rounded"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-muted rounded flex items-center justify-center text-xs text-muted-foreground">
                            N/A
                          </div>
                        )}
                        <span className="text-sm font-medium">
                          {product.targetProductName}
                        </span>
                      </div>
                      {isEditing && (
                        <div className="flex items-center space-x-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              handleReorder(type, index, 'up')
                            }
                            disabled={index === 0}
                          >
                            <ArrowUp className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              handleReorder(type, index, 'down')
                            }
                            disabled={
                              index === groupedProducts[type].length - 1
                            }
                          >
                            <ArrowDown className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              handleRemove(product.targetProductId, type)
                            }
                          >
                            <X className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            !isEditing && (
              <p className="text-gray-500 text-sm">No linked products</p>
            )
          )}

          {/* Edit mode: Add linked product section */}
          {isEditing && (
            <div className="pt-3 border-t">
              <div className="text-xs font-semibold mb-3">
                Add Linked Product
              </div>
              <div className="flex flex-col gap-3">
                <div className="flex gap-2">
                  <div
                    className="flex-1 relative"
                    onFocus={() => setIsSearchFocused(true)}
                    onBlur={(e) => {
                      setTimeout(() => {
                        if (!e.currentTarget.contains(document.activeElement)) {
                          setIsSearchFocused(false)
                        }
                      }, 200)
                    }}
                  >
                    <Input
                      placeholder="Search products by name..."
                      value={searchTerm}
                      onChange={e => handleSearch(e.target.value)}
                    />
                    {isSearchFocused && searchTerm.trim().length >= 2 && (
                      <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {searchLoading ? (
                          <div className="p-3 text-sm text-gray-500">
                            Searching...
                          </div>
                        ) : searchResults.length > 0 ? (
                          searchResults.map(result => (
                            <button
                              key={result.id}
                              type="button"
                              className="flex items-center gap-2 w-full px-3 py-2 text-left hover:bg-muted text-sm"
                              onClick={() => handleAdd(result)}
                            >
                              {result.image ? (
                                <img
                                  src={result.image}
                                  alt={result.name}
                                  className="w-8 h-8 object-cover rounded"
                                />
                              ) : (
                                <div className="w-8 h-8 bg-muted rounded flex items-center justify-center text-xs text-muted-foreground">
                                  N/A
                                </div>
                              )}
                              <span>{result.name}</span>
                            </button>
                          ))
                        ) : (
                          <div className="p-3 text-sm text-gray-500">
                            No products found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <Select
                    value={selectedLinkType}
                    onValueChange={value =>
                      setSelectedLinkType(value as LinkType)
                    }
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LINK_TYPE_OPTIONS.map(type => (
                        <SelectItem key={type} value={type}>
                          {LINK_TYPE_LABELS[type]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
